import { useState, useRef, useCallback, useEffect } from "react";
import axios from "axios";
import { jsPDF } from "jspdf";
import Disclaimer from "../components/Disclaimer";
import LoadingSpinner from "../components/LoadingSpinner";
import { useLanguage } from "../LanguageContext";

const API = process.env.REACT_APP_API_URL || "http://localhost:8001";

function ResultBadge({ result }) {
  const isAnomaly = result === "Potential Anomaly";
  return (
    <span className={`inline-flex items-center gap-1.5 px-3 py-1 rounded-full text-sm font-semibold border ${
      isAnomaly
        ? "bg-amber-50 text-amber-800 border-amber-200"
        : "bg-emerald-50 text-emerald-700 border-emerald-200"
    }`}>
      <span className={`w-2 h-2 rounded-full ${isAnomaly ? "bg-amber-500" : "bg-emerald-500"}`} />
      {result}
    </span>
  );
}

function ConfidenceBar({ value, label }) {
  const pct = Math.round(value * 100);
  return (
    <div>
      <div className="flex justify-between text-xs text-stone-500 mb-1.5">
        <span>{label}</span>
        <span className="font-medium text-stone-700">{pct}%</span>
      </div>
      <div className="h-2 bg-stone-100 rounded-full overflow-hidden">
        <div className="h-full bg-amber-500 rounded-full transition-all duration-700" style={{ width: `${pct}%` }} />
      </div>
    </div>
  );
}

function Stat({ label, value }) {
  return (
    <div className="bg-stone-50 rounded-xl p-4 border border-stone-100">
      <p className="text-xs text-stone-500 mb-1">{label}</p>
      <p className="text-sm font-semibold text-stone-800 font-mono">{value}</p>
    </div>
  );
}

function DropZone({ onFile, disabled, t }) {
  const [dragging, setDragging] = useState(false);
  const inputRef = useRef();

  const handleDrop = useCallback((e) => {
    e.preventDefault();
    setDragging(false);
    const f = e.dataTransfer.files[0];
    if (f) onFile(f);
  }, [onFile]);

  return (
    <div
      onDragOver={(e) => { e.preventDefault(); setDragging(true); }}
      onDragLeave={() => setDragging(false)}
      onDrop={handleDrop}
      onClick={() => !disabled && inputRef.current?.click()}
      className={`relative flex flex-col items-center justify-center gap-3 border-2 border-dashed rounded-2xl p-10 cursor-pointer transition-all ${
        dragging ? "border-amber-400 bg-amber-50" : "border-stone-200 bg-stone-50 hover:border-amber-300 hover:bg-amber-50"
      } ${disabled ? "opacity-50 cursor-not-allowed" : ""}`}
    >
      <div className="w-14 h-14 rounded-2xl bg-white border border-stone-200 shadow-sm flex items-center justify-center">
        <svg className="w-7 h-7 text-amber-600" fill="none" viewBox="0 0 24 24" stroke="currentColor">
          <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={1.5}
            d="M9 19V6l12-3v13M9 19c0 1.105-1.343 2-3 2s-3-.895-3-2 1.343-2 3-2 3 .895 3 2zm12-3c0 1.105-1.343 2-3 2s-3-.895-3-2 1.343-2 3-2 3 .895 3 2zM9 10l12-3" />
        </svg>
      </div>
      <div className="text-center">
        <p className="text-sm font-medium text-stone-700">
          {dragging ? t.dropHere : t.dragDrop}
        </p>
        <p className="text-xs text-stone-400 mt-1">{t.fileTypes}</p>
      </div>
      <input ref={inputRef} type="file" accept="audio/*" className="hidden"
        onChange={(e) => e.target.files[0] && onFile(e.target.files[0])} />
    </div>
  );
}

// Encode raw Float32 PCM samples into a valid WAV ArrayBuffer
function encodeWAV(samples, sampleRate) {
  const buf = new ArrayBuffer(44 + samples.length * 2);
  const view = new DataView(buf);
  const write = (off, str) => { for (let i = 0; i < str.length; i++) view.setUint8(off + i, str.charCodeAt(i)); };
  write(0, "RIFF"); view.setUint32(4, 36 + samples.length * 2, true);
  write(8, "WAVE"); write(12, "fmt ");
  view.setUint32(16, 16, true);
  view.setUint16(20, 1, true);
  view.setUint16(22, 1, true);
  view.setUint32(24, sampleRate, true);
  view.setUint32(28, sampleRate * 2, true);
  view.setUint16(32, 2, true);
  view.setUint16(34, 16, true);
  write(36, "data"); view.setUint32(40, samples.length * 2, true);
  let off = 44;
  for (let i = 0; i < samples.length; i++, off += 2) {
    const s = Math.max(-1, Math.min(1, samples[i]));
    view.setInt16(off, s < 0 ? s * 0x8000 : s * 0x7fff, true);
  }
  return buf;
}

function AudioRecorder({ onBlob, disabled, t }) {
  const [recording, setRecording] = useState(false);
  const [secs, setSecs] = useState(0);
  const ctxRef = useRef(null);
  const processorRef = useRef(null);
  const streamRef = useRef(null);
  const samplesRef = useRef([]);
  const timerRef = useRef(null);

  const start = async () => {
    try {
      const stream = await navigator.mediaDevices.getUserMedia({ audio: true });
      streamRef.current = stream;
      const ctx = new (window.AudioContext || window.webkitAudioContext)();
      ctxRef.current = ctx;
      const source = ctx.createMediaStreamSource(stream);
      const processor = ctx.createScriptProcessor(4096, 1, 1);
      processorRef.current = processor;
      samplesRef.current = [];
      processor.onaudioprocess = (e) => {
        samplesRef.current.push(new Float32Array(e.inputBuffer.getChannelData(0)));
      };
      const silent = ctx.createGain();
      silent.gain.value = 0;
      source.connect(processor);
      processor.connect(silent);
      silent.connect(ctx.destination);
      setRecording(true);
      setSecs(0);
      timerRef.current = setInterval(() => setSecs((s) => s + 1), 1000);
    } catch {
      alert(t.micDenied);
    }
  };

  const stop = () => {
    clearInterval(timerRef.current);
    processorRef.current?.disconnect();
    streamRef.current?.getTracks().forEach((tr) => tr.stop());
    const sr = ctxRef.current?.sampleRate || 44100;
    ctxRef.current?.close();
    setRecording(false);
    const chunks = samplesRef.current;
    const total = chunks.reduce((a, c) => a + c.length, 0);
    const merged = new Float32Array(total);
    let off = 0;
    for (const c of chunks) { merged.set(c, off); off += c.length; }
    const wavBuf = encodeWAV(merged, sr);
    onBlob(new Blob([wavBuf], { type: "audio/wav" }));
  };

  const fmt = (s) => `${String(Math.floor(s / 60)).padStart(2, "0")}:${String(s % 60).padStart(2, "0")}`;

  return (
    <div className="flex items-center gap-3">
      {recording ? (
        <>
          <span className="flex items-center gap-2 text-sm text-red-600 font-medium">
            <span className="w-2 h-2 rounded-full bg-red-500 animate-pulse" />
            {t.recording} {fmt(secs)}
          </span>
          <button onClick={stop}
            className="px-4 py-2 bg-red-600 hover:bg-red-700 text-white text-sm font-medium rounded-lg transition-colors">
            {t.stopAnalyze}
          </button>
        </>
      ) : (
        <button onClick={start} disabled={disabled}
          className="inline-flex items-center gap-2 px-4 py-2 bg-white border border-stone-200 hover:border-red-300 hover:text-red-600 text-stone-700 text-sm font-medium rounded-lg transition-colors disabled:opacity-40">
          <svg className="w-4 h-4" fill="none" viewBox="0 0 24 24" stroke="currentColor">
            <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2}
              d="M19 11a7 7 0 01-7 7m0 0a7 7 0 01-7-7m7 7v4m0 0H8m4 0h4m-4-8a3 3 0 01-3-3V5a3 3 0 116 0v6a3 3 0 01-3 3z" />
          </svg>
          {t.recordMic}
        </button>
      )}
    </div>
  );
}

export default function VoiceAnalyzer() {
  const { t, lang } = useLanguage();
  const v = t.voice;
  const [file, setFile] = useState(null);
  const [loading, setLoading] = useState(false);
  const [result, setResult] = useState(null);
  const [error, setError] = useState(null);

  const [audioUrl, setAudioUrl] = useState(null);

  useEffect(() => {
    if (!file) { setAudioUrl(null); return; }
    const url = URL.createObjectURL(file);
    setAudioUrl(url);
    return () => URL.revokeObjectURL(url);
  }, [file]);

  const downloadPDF = () => {
    const doc = new jsPDF();
    const addText = (text, x, startY, maxW, lh) => {
      const lines = doc.splitTextToSize(String(text), maxW);
      let y = startY;
      for (const line of lines) {
        if (y > 275) { doc.addPage(); y = 20; }
        doc.text(line, x, y);
        y += lh;
      }
      return y;
    };
    doc.setFillColor(180, 83, 9);
    doc.rect(0, 0, 210, 22, "F");
    doc.setTextColor(255, 255, 255);
    doc.setFontSize(15);
    doc.setFont("helvetica", "bold");
    doc.text("WorryLens \u2014 Voice Analysis Report", 14, 14);
    doc.setTextColor(40, 40, 40);
    doc.setFont("helvetica", "normal");
    doc.setFontSize(9);
    let y = 32;
    doc.text(`Date: ${new Date().toLocaleString()}`, 14, y); y += 6;
    doc.text(`File: ${file?.name || "recording.wav"}`, 14, y); y += 10;
    doc.setFont("helvetica", "bold"); doc.setFontSize(11);
    doc.text("Assessment", 14, y); y += 6;
    doc.setFont("helvetica", "normal"); doc.setFontSize(9);
    doc.text(`Result: ${result.result}`, 14, y); y += 6;
    doc.text(`Confidence: ${Math.round(result.confidence * 100)}%`, 14, y); y += 6;
    if (result.anomaly_flags?.length) {
      y = addText(`Anomaly Flags: ${result.anomaly_flags.map(f => f.replace(/_/g, " ")).join(", ")}`, 14, y, 182, 5);
    }
    y += 6;
    doc.setFont("helvetica", "bold"); doc.setFontSize(11);
    doc.text("Vocal Features", 14, y); y += 6;
    doc.setFont("helvetica", "normal"); doc.setFontSize(9);
    doc.text(`Pitch Mean: ${result.features.pitch_mean} Hz`, 14, y); y += 6;
    doc.text(`Pitch Variability: ${result.features.pitch_std}`, 14, y); y += 6;
    doc.text(`Vocal Energy: ${result.features.energy}`, 14, y); y += 6;
    doc.text(`MFCC Mean: ${result.features.mfcc_mean}`, 14, y); y += 6;
    doc.text(`Zero Crossing Rate: ${result.features.zcr}`, 14, y); y += 6;
    doc.text(`Spectral Centroid: ${result.features.spectral_centroid} Hz`, 14, y); y += 10;
    doc.setFont("helvetica", "bold"); doc.setFontSize(11);
    doc.text("AI Analysis", 14, y); y += 6;
    doc.setFont("helvetica", "normal"); doc.setFontSize(9);
    y = addText(result.explanation, 14, y, 182, 5); y += 10;
    doc.setTextColor(120, 120, 120); doc.setFontSize(8);
    addText("This is a health screening tool only \u2014 not a medical diagnosis. Please consult a qualified healthcare professional for any medical concerns.", 14, y, 182, 4.5);
    doc.save(`worrylens-voice-${Date.now()}.pdf`);
  };

  const saveToHistory = (data, filename) => {
    const history = JSON.parse(localStorage.getItem("wl_history") || "[]");
    history.unshift({
      id: Date.now(), type: "voice", filename,
      date: new Date().toISOString(),
      result: data.result, confidence: data.confidence,
      explanation: data.explanation, features: data.features,
      anomaly_flags: data.anomaly_flags,
    });
    localStorage.setItem("wl_history", JSON.stringify(history.slice(0, 20)));
  };

  const analyze = async (audioFile) => {
    setLoading(true); setError(null); setResult(null);
    const form = new FormData();
    form.append("file", audioFile, audioFile.name || "recording.wav");
    form.append("language", lang);
    try {
      const { data } = await axios.post(`${API}/analyze-voice`, form);
      setResult(data);
      saveToHistory(data, audioFile.name || "recording.wav");
    } catch (err) {
      setError(err.response?.data?.detail || v.errorFallback);
    } finally {
      setLoading(false);
    }
  };

  const handleFile = (f) => { setFile(f); setResult(null); setError(null); };
  const handleBlob = (blob) => {
    const f = new File([blob], "recording.wav", { type: "audio/wav" });
    setFile(f);
    analyze(f);
  };

  return (
    <div className="max-w-3xl mx-auto px-4 sm:px-6 py-10 animate-fade-in">
      <div className="mb-8">
        <div className="flex items-center gap-3 mb-2">
          <div className="w-10 h-10 rounded-xl bg-amber-700 flex items-center justify-center">
            <svg className="w-5 h-5 text-white" fill="none" viewBox="0 0 24 24" stroke="currentColor">
              <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2}
                d="M19 11a7 7 0 01-7 7m0 0a7 7 0 01-7-7m7 7v4m0 0H8m4 0h4m-4-8a3 3 0 01-3-3V5a3 3 0 116 0v6a3 3 0 01-3 3z" />
            </svg>
          </div>
          <div>
            <h1 className="text-2xl font-bold text-stone-900">{v.title}</h1>
            <p className="text-sm text-stone-500">{v.subtitle}</p>
          </div>
        </div>
      </div>

      <Disclaimer className="mb-6" />

      <div className="bg-white rounded-2xl border border-stone-200 shadow-sm p-6 mb-6">
        <h2 className="font-semibold text-stone-800 mb-4">{v.uploadTitle}</h2>
        <DropZone onFile={handleFile} disabled={loading} t={v} />

        <div className="flex items-center gap-3 my-4">
          <hr className="flex-1 border-stone-100" />
          <span className="text-xs text-stone-400">or</span>
          <hr className="flex-1 border-stone-100" />
        </div>

        <AudioRecorder onBlob={handleBlob} disabled={loading} t={v} />

        {file && !loading && (
          <div className="mt-4 space-y-3">
            <div className="flex items-center justify-between bg-amber-50 border border-amber-100 rounded-xl px-4 py-3">
              <div className="flex items-center gap-2 text-sm">
                <svg className="w-4 h-4 text-amber-600" fill="none" viewBox="0 0 24 24" stroke="currentColor">
                  <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2}
                    d="M9 19V6l12-3v13M9 19c0 1.105-1.343 2-3 2s-3-.895-3-2 1.343-2 3-2 3 .895 3 2zm12-3c0 1.105-1.343 2-3 2s-3-.895-3-2 1.343-2 3-2 3 .895 3 2zM9 10l12-3" />
                </svg>
                <span className="text-stone-700 font-medium">{file.name}</span>
                <span className="text-stone-400">({(file.size / 1024).toFixed(0)} KB)</span>
              </div>
              <button onClick={() => analyze(file)}
                className="px-4 py-2 bg-amber-700 hover:bg-amber-800 text-white text-sm font-semibold rounded-lg transition-colors">
                {v.analyze}
              </button>
            </div>
            {audioUrl && (
              <audio controls src={audioUrl} className="w-full rounded-xl" />
            )}
          </div>
        )}
      </div>

      {loading && (
        <div className="bg-white rounded-2xl border border-stone-200 shadow-sm">
          <LoadingSpinner message={v.loading} />
        </div>
      )}

      {error && (
        <div className="bg-rose-50 border border-rose-200 rounded-2xl p-4 text-sm text-rose-700">
          <strong>{v.error}:</strong> {error}
        </div>
      )}

      {result && !loading && (
        <div className="space-y-5 animate-slide-up">
          <div className="bg-white rounded-2xl border border-stone-200 shadow-sm p-6">
            <div className="flex items-start justify-between mb-5">
              <div>
                <p className="text-xs text-stone-500 font-medium uppercase tracking-wide mb-1">{v.result}</p>
                <ResultBadge result={result.result} />
              </div>
              <div className="flex flex-col items-end gap-2">
                <div className="text-right">
                  <p className="text-xs text-stone-500 mb-1">{v.file}</p>
                  <p className="text-sm text-stone-600 font-medium">{file?.name || "recording.wav"}</p>
                </div>
                <button onClick={downloadPDF}
                  className="inline-flex items-center gap-1.5 px-3 py-1.5 bg-stone-100 hover:bg-stone-200 text-stone-700 text-xs font-medium rounded-lg transition-colors">
                  <svg className="w-3.5 h-3.5" fill="none" viewBox="0 0 24 24" stroke="currentColor">
                    <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M4 16v1a3 3 0 003 3h10a3 3 0 003-3v-1m-4-4l-4 4m0 0l-4-4m4 4V4" />
                  </svg>
                  Download PDF
                </button>
              </div>
            </div>

            <ConfidenceBar value={result.confidence} label={v.confidence} />

            {result.anomaly_flags && result.anomaly_flags.length > 0 && (
              <div className="mt-4">
                <p className="text-xs text-stone-500 mb-2">{v.anomalyFlags}</p>
                <div className="flex flex-wrap gap-2">
                  {result.anomaly_flags.map((f) => (
                    <span key={f} className="text-xs bg-amber-50 text-amber-800 border border-amber-200 px-2.5 py-1 rounded-full font-medium">
                      {f.replace(/_/g, " ")}
                    </span>
                  ))}
                </div>
              </div>
            )}
          </div>

          <div className="bg-white rounded-2xl border border-stone-200 shadow-sm p-6">
            <h3 className="font-semibold text-stone-800 mb-4 text-sm uppercase tracking-wide">{v.extractedFeatures}</h3>
            <div className="grid grid-cols-2 sm:grid-cols-3 gap-3">
              <Stat label={v.pitchMean}       value={`${result.features.pitch_mean ?? '—'} Hz`} />
              <Stat label={v.pitchVariability} value={result.features.pitch_std ?? '—'} />
              <Stat label={v.vocalEnergy}     value={result.features.energy ?? '—'} />
              <Stat label={v.mfccMean}        value={result.features.mfcc_mean ?? '—'} />
              <Stat label={v.zeroCrossing}    value={result.features.zcr ?? '—'} />
              <Stat label={v.spectralCentroid} value={`${result.features.spectral_centroid ?? '—'} Hz`} />
            </div>
          </div>

          <div className="bg-white rounded-2xl border border-stone-200 shadow-sm p-6">
            <div className="flex items-center gap-2 mb-4">
              <div className="w-6 h-6 rounded-full bg-amber-700 flex items-center justify-center">
                <svg className="w-3.5 h-3.5 text-white" fill="currentColor" viewBox="0 0 24 24">
                  <path d="M12 2C6.48 2 2 6.48 2 12s4.48 10 10 10 10-4.48 10-10S17.52 2 12 2zm-2 14.5v-9l6 4.5-6 4.5z" />
                </svg>
              </div>
              <h3 className="font-semibold text-stone-800">{v.aiExplanation}</h3>
              <span className="text-xs text-stone-400 ml-auto">{v.poweredBy}</span>
            </div>
            <div className="text-sm text-stone-700 leading-relaxed whitespace-pre-wrap">{result.explanation}</div>
          </div>
        </div>
      )}
    </div>
  );
}
